import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Product } from "../lib/types";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

interface ProductCardProps {
  product: Product;
  index: number;
}

const ProductCard = ({ product, index }: ProductCardProps) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  return (
    <motion.div 
      ref={ref}
      className="bg-white border border-neutral-100 rounded-lg overflow-hidden shadow-md"
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ 
        y: -10,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
      }}
    >
      <div className="overflow-hidden">
        <motion.img 
          src={product.imageUrl} 
          alt={product.name} 
          className="w-full h-56 object-cover"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.3 }}
        />
      </div>
      <div className="p-6">
        <span className="text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full inline-block">
          {product.category}
        </span>
        <h3 className="text-xl font-bold mt-3 mb-2 font-heading">{product.name}</h3>
        <p className="text-neutral-700 mb-4">{product.description}</p>
        <div className="flex items-center justify-between">
          <Link href={`/products/${product.slug}`}>
            <motion.span 
              className="text-primary font-medium flex items-center gap-2 cursor-pointer"
              whileHover={{ x: 5 }}
            >
              View Details 
              <motion.span
                initial={{ x: 0 }}
                whileHover={{ x: 5 }}
                transition={{ duration: 0.3 }}
              >
                <ArrowRight className="h-4 w-4" />
              </motion.span>
            </motion.span>
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

interface ProductsSectionProps {
  products: Product[];
}

const ProductsSection = ({ products }: ProductsSectionProps) => {
  const [activeCategory, setActiveCategory] = useState("All Seeds");
  const [sectionRef, sectionInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const categories = [
    "All Seeds",
    "Cereals",
    "Pulses",
    "Vegetables",
    "Oil Seeds"
  ];

  const filteredProducts = activeCategory === "All Seeds" 
    ? products 
    : products.filter(product => product.category === activeCategory);
    
  return (
    <section id="products" className="py-20 bg-white overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div 
          ref={sectionRef}
          className="text-center max-w-3xl mx-auto mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={sectionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <motion.span 
            className="text-primary font-medium inline-block"
            initial={{ opacity: 0 }}
            animate={sectionInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Our Products
          </motion.span>
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mt-2 mb-4 font-heading"
            initial={{ opacity: 0 }}
            animate={sectionInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Premium Seed Collection
          </motion.h2>
          <motion.p 
            className="text-neutral-700 mb-8"
            initial={{ opacity: 0 }}
            animate={sectionInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Discover our wide range of high-yielding, disease-resistant seed 
            varieties for diverse agricultural needs.
          </motion.p>
          
          <motion.div 
            className="inline-flex flex-wrap justify-center gap-2 mb-8"
            initial={{ opacity: 0 }}
            animate={sectionInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {categories.map((category, index) => (
              <motion.button
                key={index}
                className={`px-6 py-2 rounded-full font-medium transition ${
                  activeCategory === category
                    ? "bg-primary text-white"
                    : "bg-neutral-100 hover:bg-neutral-200 text-neutral-900"
                }`}
                onClick={() => setActiveCategory(category)}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.95 }}
              >
                {category}
              </motion.button>
            ))}
          </motion.div>
        </motion.div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts.map((product, index) => (
            <ProductCard key={index} product={product} index={index} />
          ))}
        </div>
        
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={sectionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <Link href="/products">
            <Button className="bg-primary hover:bg-primary/90 text-white rounded-full">
              View All Products
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductsSection;